import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckSquare, Plus, LayoutList, LayoutGrid, Calendar as CalendarIcon, ChevronDown, Search, FolderTree, Loader2, Zap, Filter, Columns, CheckCircle, Edit, UserPlus, Flag, Trash2, Eye, ArrowRight } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useTasksTour } from '@/hooks/use-tasks-tour';
import { TasksListView } from '@/components/tasks/TasksListView';
import { TasksBoardView } from '@/components/tasks/TasksBoardView';
import { TasksCalendarView } from '@/components/tasks/TasksCalendarView';
import { TasksFiltersTab } from '@/components/RightPanel/TasksFiltersTab';
import { CreateTaskModal } from '@/components/tasks/CreateTaskModal';
import { dataService, isDemoMode } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import type { Task, TaskStatus, TaskPriority } from '@/types/task';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TasksColumnsTab } from '@/components/RightPanel/TasksColumnsTab';
import { DEFAULT_TASKS_COLUMNS } from '@/lib/tasks-columns-config';
import { cn } from '@/lib/utils';
import { LiveModePlaceholder, TableSkeleton } from '@/components/shared/live-mode-placeholder';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  bulkUpdateStatus,
  bulkUpdatePriority,
  bulkAssign,
  bulkDeleteTasks,
} from '@/services/tasks-service';

type ViewMode = 'list' | 'board' | 'calendar';

const STATUS_OPTIONS: TaskStatus[] = ['New', 'In Progress', 'Blocked', 'Resolved', 'Closed'];
const PRIORITY_OPTIONS: TaskPriority[] = ['Urgent', 'High', 'Medium', 'Low'];

// Helper function to check if task matches search query - extracted to reduce cognitive complexity (S3776)
function taskMatchesSearch(task: Task, searchQuery: string): boolean {
  if (!searchQuery) return true;
  const query = searchQuery.toLowerCase();
  return (
    task.title.toLowerCase().includes(query) ||
    (task.description?.toLowerCase().includes(query) ?? false) ||
    (task.assignee?.name.toLowerCase().includes(query) ?? false)
  );
}

// Helper function to check if task is overdue - extracted to reduce cognitive complexity (S3776)
function isTaskOverdue(task: Task): boolean {
  return Boolean(task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Resolved' && task.status !== 'Closed');
}

// Helper function to check if task is due today - extracted to reduce cognitive complexity (S3776)
function isTaskDueToday(task: Task): boolean {
  if (!task.dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const taskDue = new Date(task.dueDate);
  return taskDue >= today && taskDue < tomorrow;
}

// Helper function to check if task is due this week - extracted to reduce cognitive complexity (S3776)
function isTaskDueThisWeek(task: Task): boolean {
  if (!task.dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  const taskDue = new Date(task.dueDate);
  return taskDue >= today && taskDue < nextWeek;
}

// Helper function to check if task matches quick filter - extracted to reduce cognitive complexity (S3776)
function taskMatchesQuickFilter(task: Task, activeQuickFilter: string | null): boolean {
  if (!activeQuickFilter) return true;
  
  if (activeQuickFilter === 'overdue') {
    return isTaskOverdue(task);
  }
  if (activeQuickFilter === 'dueToday') {
    return isTaskDueToday(task);
  }
  if (activeQuickFilter === 'dueThisWeek') {
    return isTaskDueThisWeek(task);
  }
  if (activeQuickFilter === 'blocked') {
    return task.status === 'Blocked';
  }
  if (activeQuickFilter === 'urgent') {
    return task.priority === 'Urgent';
  }
  // 'myTasks' filter - placeholder for now
  return true;
}

// Helper function to check if task matches table filters - extracted to reduce cognitive complexity (S3776)
function taskMatchesTableFilters(task: Task, tableFilters: Record<string, unknown>): boolean {
  if (tableFilters.status && Array.isArray(tableFilters.status) && tableFilters.status.length > 0) {
    if (!tableFilters.status.includes(task.status)) return false;
  }
  if (tableFilters.priority && Array.isArray(tableFilters.priority) && tableFilters.priority.length > 0) {
    if (!tableFilters.priority.includes(task.priority)) return false;
  }
  if (tableFilters.type && Array.isArray(tableFilters.type) && tableFilters.type.length > 0) {
    if (!tableFilters.type.includes(task.type)) return false;
  }
  return true;
}

export function TasksHome() {
  const navigate = useNavigate();
  const { mode } = useMode();
  useFeatureSettings(); // Hook called for side effects
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useTasksTour();
  const { setOnStartTour } = useTour();
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [boardGroupBy, setBoardGroupBy] = useState<'status' | 'assignee' | 'priority' | 'type'>('status');

  // Bulk action dialog states
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isPriorityDialogOpen, setIsPriorityDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus>('Resolved');
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority>('Medium');
  const [assigneeInput, setAssigneeInput] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    tableFilters,
    isPinned,
    visibleColumns: contextVisibleColumns,
    isApplyingFilters
  } = useRightPanel();

  // Quick filters - single selection only (radio-style)
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);

  const quickFilters = [
    { id: 'myTasks', name: 'My Tasks', icon: '👤' },
    { id: 'overdue', name: 'Overdue', icon: '🔴', color: 'text-red-600' },
    { id: 'dueToday', name: 'Due Today', icon: '📅' },
    { id: 'dueThisWeek', name: 'Due This Week', icon: '📆' },
    { id: 'blocked', name: 'Blocked', icon: '🚫' },
    { id: 'urgent', name: 'Urgent', icon: '⚡', color: 'text-orange-600' },
  ];

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => {
      setOnStartTour(null);
    };
  }, [setOnStartTour, startTour]);

  // Reset filters on mount
  useEffect(() => {
    // Reset all local filters on mount
    setActiveQuickFilter(null);
    setSearchQuery('');
  }, []); // Only run on mount

  const handleQuickFilterToggle = (filterId: string) => {
    // If clicking the active filter, deselect it
    if (activeQuickFilter === filterId) {
      setActiveQuickFilter(null);
    } else {
      // Otherwise, select this filter (single selection)
      setActiveQuickFilter(filterId);
    }
  };

  // Load tasks
  useEffect(() => {
    async function loadTasks() {
      try {
        setLoading(true);
        setError(null);
        const result = await dataService.getTasks();
        // getTasks returns an object with { data, total, page, pageSize, totalPages }
        setTasks(Array.isArray(result) ? result : result.data ?? []);
      } catch (err) {
        console.error('Failed to load tasks:', err);
        if (mode === 'live') {
          setError('Failed to load tasks from API. Please check your connection.');
          setTasks([]);
        }
      } finally {
        setLoading(false);
      }
    }
    loadTasks();
  }, [mode]);

  // Filter tasks using extracted helper functions to reduce cognitive complexity (S3776)
  const filteredTasks = tasks.filter((task) => {
    // Use extracted helper functions for filtering
    if (!taskMatchesSearch(task, searchQuery)) return false;
    if (!taskMatchesQuickFilter(task, activeQuickFilter)) return false;
    if (!taskMatchesTableFilters(task, tableFilters)) return false;
    return true;
  });

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  // Reload tasks after bulk action
  const reloadTasks = useCallback(async () => {
    try {
      const result = await dataService.getTasks();
      setTasks(Array.isArray(result) ? result : result.data || []);
    } catch (err) {
      console.error('Failed to reload tasks:', err);
    }
  }, []);

  // Task update handler - extracted to reduce nesting depth
  const handleTaskUpdate = useCallback((task: Task) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
  }, []);

  // Bulk action handlers
  const handleMarkAsComplete = async () => {
    setBulkActionLoading(true);
    try {
      const workItemIds = Array.from(selectedRowIds);
      await bulkUpdateStatus({ workItemIds, status: 'Resolved' });
      await reloadTasks();
      setSelectedRowIds(new Set());
      setIsExpanded(false);
    } catch (error) {
      console.error('Failed to mark tasks as complete:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusChange = async () => {
    setBulkActionLoading(true);
    try {
      const workItemIds = Array.from(selectedRowIds);
      await bulkUpdateStatus({ workItemIds, status: selectedStatus });
      await reloadTasks();
      setSelectedRowIds(new Set());
      setIsExpanded(false);
      setIsStatusDialogOpen(false);
    } catch (error) {
      console.error('Failed to change task status:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkPriorityChange = async () => {
    setBulkActionLoading(true);
    try {
      const workItemIds = Array.from(selectedRowIds);
      await bulkUpdatePriority({ workItemIds, priority: selectedPriority });
      await reloadTasks();
      setSelectedRowIds(new Set());
      setIsExpanded(false);
      setIsPriorityDialogOpen(false);
    } catch (error) {
      console.error('Failed to change task priority:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    setBulkActionLoading(true);
    try {
      const workItemIds = Array.from(selectedRowIds);
      await bulkAssign({
        workItemIds,
        assignedToUserId: assigneeInput || undefined,
        assignedToUserName: assigneeInput || undefined,
      });
      await reloadTasks();
      setSelectedRowIds(new Set());
      setIsExpanded(false);
      setIsAssignDialogOpen(false);
      setAssigneeInput('');
    } catch (error) {
      console.error('Failed to assign tasks:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const workItemIds = Array.from(selectedRowIds);
      await bulkDeleteTasks({ workItemIds });
      await reloadTasks();
      setSelectedRowIds(new Set());
      setIsExpanded(false);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete work items:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-xs text-muted-foreground">
              Select one or more tasks to see available actions
            </p>
          </div>
        ) : (
          <>
            {/* Bulk Actions */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Bulk Actions
              </h4>
              <ActionCard
                icon={CheckCircle}
                title="Mark as Complete"
                description={`Mark ${selectedRowIds.size} task${selectedRowIds.size !== 1 ? 's' : ''} as complete`}
                variant="action"
                onClick={handleMarkAsComplete}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={Edit}
                title="Change Status"
                description="Update status for selected tasks"
                variant="action"
                onClick={() => setIsStatusDialogOpen(true)}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={UserPlus}
                title="Assign to User"
                description="Assign selected tasks to a user"
                variant="action"
                onClick={() => setIsAssignDialogOpen(true)}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={Flag}
                title="Change Priority"
                description="Update priority level for tasks"
                variant="action"
                onClick={() => setIsPriorityDialogOpen(true)}
                disabled={bulkActionLoading}
              />
            </div>

            {/* Other Actions */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Other Actions
              </h4>
              <ActionCard
                icon={Trash2}
                title="Delete Work Items"
                description={`Permanently remove ${selectedRowIds.size} task${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="delete"
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={bulkActionLoading}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsForBadge = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_TASKS_COLUMNS.filter(col => col.visible).map(col => col.id);

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <TasksFiltersTab />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <TasksColumnsTab viewMode={viewMode} onViewModeChange={setViewMode} />,
      badgeCount: visibleColumnsForBadge.length,
    },
  ];

  const getViewIcon = () => {
    switch (viewMode) {
      case 'list':
        return <LayoutList className="h-4 w-4" />;
      case 'board':
        return <LayoutGrid className="h-4 w-4" />;
      case 'calendar':
        return <CalendarIcon className="h-4 w-4" />;
    }
  };

  const getViewLabel = () => {
    switch (viewMode) {
      case 'list':
        return 'List View';
      case 'board':
        return 'Board View';
      case 'calendar':
        return 'Calendar View';
    }
  };

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col">
        {/* Page Header - Sticky */}
        <div className="sticky top-0 z-20 border-b bg-card p-6 flex-shrink-0" data-tour="tasks-header">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight">Work Items</h1>
                  {isDemoMode() && (
                    <Badge variant="secondary" className="text-xs">
                      Demo Mode
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage and track tasks across your organisation
                </p>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-3">
              {/* View Mode Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-tour="view-mode-selector">
                    {getViewIcon()}
                    <span className="ml-2">{getViewLabel()}</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setViewMode('list')}>
                    <LayoutList className="mr-2 h-4 w-4" />
                    List View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setViewMode('board')}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Board View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setViewMode('calendar')}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Calendar View
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button onClick={() => setIsCreateModalOpen(true)} data-tour="new-task-button">
                <Plus className="mr-2 h-4 w-4" />
                New Task
              </Button>
            </div>
          </div>
        </div>

        {/* Search Bar and Quick Filters Panel */}
        <div className="border-b bg-card px-6 py-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Search Bar - Smaller width with white background */}
            <div className="relative w-80" data-tour="search-tasks">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-950"
              />
            </div>

            {/* Group By Dropdown - Only show in board view */}
            {viewMode === 'board' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <FolderTree className="h-4 w-4" />
                    Group By: {boardGroupBy.charAt(0).toUpperCase() + boardGroupBy.slice(1)}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setBoardGroupBy('status')}>
                    Status
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBoardGroupBy('assignee')}>
                    Assignee
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBoardGroupBy('priority')}>
                    Priority
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBoardGroupBy('type')}>
                    Type
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Quick Filters - Horizontal badges to the right */}
            <div className="flex items-center gap-2 flex-1 overflow-x-auto" data-tour="quick-filters">
              {quickFilters.map((filter) => (
                <Badge
                  key={filter.id}
                  variant={activeQuickFilter === filter.id ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer hover:bg-primary/90 transition-colors px-3 py-1.5 whitespace-nowrap',
                    activeQuickFilter === filter.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted',
                    filter.color && activeQuickFilter !== filter.id && filter.color
                  )}
                  onClick={() => handleQuickFilterToggle(filter.id)}
                >
                  <span className={cn('mr-1.5', filter.color && activeQuickFilter !== filter.id && filter.color)}>
                    {filter.icon}
                  </span>
                  {filter.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area - Full page scroll for board view */}
        <div className={cn(
          "flex-1 flex flex-col min-h-0",
          viewMode === 'board' ? 'overflow-auto' : 'overflow-hidden'
        )} data-tour="tasks-list">
          {(() => {
            if (loading || isApplyingFilters) {
              return (
                <div className="px-6 py-4">
                  <TableSkeleton rows={10} />
                </div>
              );
            }
            if (error && mode === 'live') {
              return (
                <LiveModePlaceholder
                  variant="error"
                  title="Failed to load work items"
                  description={error}
                  onRetry={() => window.location.reload()}
                  isLoading={loading}
                />
              );
            }
            if (mode === 'live' && tasks.length === 0 && !searchQuery && !activeQuickFilter) {
              return (
                <LiveModePlaceholder
                  variant="empty"
                  title="No work items found"
                  description="The API returned no work items. This could mean the database is empty or there's a connection issue."
                  onRetry={() => window.location.reload()}
                  isLoading={loading}
                />
              );
            }
            return (
            <>
              {viewMode === 'list' && (
                <TasksListView
                  tasks={filteredTasks}
                  onTaskClick={(taskId) => navigate(`/tasks/${taskId}`)}
                  selectedRowIds={selectedRowIds}
                  onSelectionChange={handleSelectionChange}
                  visibleColumns={contextVisibleColumns && contextVisibleColumns.length > 0 
                    ? contextVisibleColumns 
                    : DEFAULT_TASKS_COLUMNS.filter(col => col.visible).map(col => col.id)}
                />
              )}
              {viewMode === 'board' && (
                <TasksBoardView
                  tasks={filteredTasks}
                  onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
                  onTaskUpdate={handleTaskUpdate}
                  groupBy={boardGroupBy}
                />
              )}
              {viewMode === 'calendar' && (
                <TasksCalendarView
                  tasks={filteredTasks}
                  onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
                  onTaskUpdate={handleTaskUpdate}
                />
              )}
            </>
            );
          })()}
        </div>
      </div>

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} data-tour="right-panel" />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onTaskCreated={(newTask) => {
          setTasks((prev) => [newTask as Task, ...prev]);
        }}
      />

      {/* Change Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Select a new status for {selectedRowIds.size} selected task{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedStatus} onValueChange={(value: TaskStatus) => setSelectedStatus(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkStatusChange} disabled={bulkActionLoading}>
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Priority Dialog */}
      <Dialog open={isPriorityDialogOpen} onOpenChange={setIsPriorityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Priority</DialogTitle>
            <DialogDescription>
              Select a new priority for {selectedRowIds.size} selected task{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPriority} onValueChange={(value: TaskPriority) => setSelectedPriority(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPriorityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkPriorityChange} disabled={bulkActionLoading}>
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to User Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to User</DialogTitle>
            <DialogDescription>
              Enter the name of the user to assign {selectedRowIds.size} selected task{selectedRowIds.size !== 1 ? 's' : ''} to.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter user name"
              value={assigneeInput}
              onChange={(e) => setAssigneeInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkAssign} disabled={bulkActionLoading}>
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Items</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRowIds.size} selected work item{selectedRowIds.size !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </PageWithRightPanel>
  );
}

export default TasksHome;
